import {
  Alert,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import DownloadIcon from '@mui/icons-material/Download'
import SendIcon from '@mui/icons-material/Send'
import { useState } from 'react'
import { api } from '@/api/client'
import {
  useCancelIrn,
  useEInvoice,
  useEInvoiceSettings,
  useRecordEInvoiceFailure,
  useRecordIrn,
  useRequestIrn,
} from '@/api/hooks'
import type { Invoice } from '@/api/types'
import { ErrorBanner, formatDateTime } from '@/components/common'

/** The colour each standing is read at a glance by. */
const TONE: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  NOT_REQUIRED: 'default',
  PENDING: 'warning',
  REGISTERED: 'success',
  CANCELLED: 'error',
  FAILED: 'error',
}

const LABEL: Record<string, string> = {
  NOT_REQUIRED: 'Not required',
  PENDING: 'Awaiting IRN',
  REGISTERED: 'Registered',
  CANCELLED: 'IRN cancelled',
  FAILED: 'Portal refused',
}

/** A short chip for the invoice list. Renders nothing on a B2C bill. */
export function EInvoiceChip({ invoice }: { invoice: Invoice }) {
  if (!invoice.eInvoiceRequired) return null
  return (
    <Chip
      size="small"
      variant={invoice.eInvoiceStatus === 'REGISTERED' ? 'filled' : 'outlined'}
      color={TONE[invoice.eInvoiceStatus] ?? 'default'}
      label={LABEL[invoice.eInvoiceStatus] ?? invoice.eInvoiceStatus}
    />
  )
}

/**
 * E-invoicing for a tax invoice raised on a customer registered under GST.
 *
 * <p>The portal is the government's, not ours: this produces the file it wants
 * in its own schema and takes down what it returned. The IRN and the signed QR
 * are computed and signed at the portal, so they are only ever copied in - and
 * once they are, they print on the invoice, which is what makes it valid.</p>
 */
export function EInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice | null
  onClose: () => void
}) {
  const { data: status, error } = useEInvoice(invoice?.id)
  const { data: settings } = useEInvoiceSettings()
  const record = useRecordIrn()
  const cancel = useCancelIrn()
  const failure = useRecordEInvoiceFailure()
  const request = useRequestIrn()

  const [irn, setIrn] = useState('')
  const [ackNo, setAckNo] = useState('')
  const [ackDate, setAckDate] = useState('')
  const [signedQrCode, setSignedQrCode] = useState('')
  const [ewbNo, setEwbNo] = useState('')
  const [cancelReason, setCancelReason] = useState('2')
  const [cancelRemarks, setCancelRemarks] = useState('')
  const [refusal, setRefusal] = useState('')

  if (!invoice) return null

  const registered = status?.status === 'REGISTERED'
  // A cancelled IRN is the end of this document: the portal will not register
  // the same number twice, so nothing here offers to try again.
  const cancelled = status?.status === 'CANCELLED'
  const busy = record.isPending || cancel.isPending || failure.isPending || request.isPending

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>E-invoice — {invoice.invoiceNumber}</span>
          {status ? (
            <Chip
              size="small"
              color={TONE[status.status] ?? 'default'}
              label={LABEL[status.status] ?? status.status}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={error ?? record.error ?? cancel.error ?? failure.error ?? request.error} />

        {status && !status.required ? (
          <Alert severity="info">
            {invoice.customerName} has no GSTIN on file, so this is a B2C supply and needs no IRN. Add
            their GSTIN to the customer if they are registered and raise the invoice again.
          </Alert>
        ) : null}

        {status?.required ? (
          <>
            <Alert severity={registered ? 'success' : cancelled ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {registered
                ? 'Registered. The IRN and QR below print on the invoice.'
                : cancelled
                  ? 'The IRN on this invoice was cancelled at the portal. It cannot be registered '
                    + 'again under the same number — raise a fresh invoice.'
                  : 'A tax invoice to a customer registered under GST is not valid until the portal '
                    + 'has signed it. Ask the portal directly below, or download the file, upload it '
                    + 'yourself, and record what comes back.'}
            </Alert>

            {!registered && !cancelled ? (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  disabled={busy}
                  onClick={() => request.mutate(invoice.id)}
                >
                  {request.isPending ? 'Asking the portal…' : 'Get IRN from the portal'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Needs the e-invoice account under API configuration filled in and switched on
                </Typography>
              </Stack>
            ) : null}

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() =>
                  api.download(
                    `/api/v1/invoices/${invoice.id}/e-invoice/payload`,
                    undefined,
                    `${invoice.invoiceNumber.replace(/\//g, '-')}-einvoice.json`,
                  )
                }
              >
                Download portal file (INV-01)
              </Button>
              {/* Straight to wherever this company files, as set under Settings. */}
              {settings?.portalUrl ? (
                <Link href={settings.portalUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                  Open {settings.portalName || 'the portal'}
                </Link>
              ) : null}
            </Stack>

            {registered ? (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    IRN
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {status.irn}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Ack No
                  </Typography>
                  <Typography variant="body2">{status.ackNo ?? '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Acknowledged
                  </Typography>
                  <Typography variant="body2">{formatDateTime(status.ackDate)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Cancellable until
                  </Typography>
                  <Typography variant="body2">{formatDateTime(status.cancellableUntil)}</Typography>
                </Grid>
              </Grid>
            ) : null}

            {status.status === 'FAILED' && status.errorMessage ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {status.errorCode ? `${status.errorCode}: ` : ''}
                {status.errorMessage}
              </Alert>
            ) : null}

            {status.status === 'CANCELLED' ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                The IRN was cancelled at the portal on {formatDateTime(status.cancelledAt)}
                {status.cancelRemarks ? ` — ${status.cancelRemarks}` : ''}. Raise a fresh invoice.
              </Alert>
            ) : null}

            {registered || !cancelled ? <Divider sx={{ my: 2 }} /> : null}

            {cancelled ? null : registered ? (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Cancel the IRN
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Only within a day of the acknowledgement, and only after cancelling it at the
                  portal. After that the correction is a credit note.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
                  <TextField
                    select
                    size="small"
                    label="Reason"
                    value={cancelReason}
                    onChange={(event) => setCancelReason(event.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {(status.cancelReasons ?? []).map((reason) => (
                      <MenuItem key={reason.code} value={reason.code}>
                        {reason.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    label="Remarks"
                    value={cancelRemarks}
                    onChange={(event) => setCancelRemarks(event.target.value)}
                  />
                  <Button
                    color="error"
                    disabled={busy}
                    onClick={() =>
                      cancel.mutate({
                        id: invoice.id,
                        reasonCode: cancelReason,
                        remarks: cancelRemarks.trim() || undefined,
                      })
                    }
                  >
                    Record cancellation
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Record what the portal returned
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="IRN"
                      placeholder="64 characters, exactly as the portal returned it"
                      value={irn}
                      onChange={(event) => setIrn(event.target.value)}
                      helperText={`${irn.trim().length}/64`}
                      error={irn.trim().length > 0 && irn.trim().length !== 64}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Ack No"
                      value={ackNo}
                      onChange={(event) => setAckNo(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="Ack date"
                      value={ackDate}
                      onChange={(event) => setAckDate(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="E-way bill no"
                      value={ewbNo}
                      onChange={(event) => setEwbNo(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={3}
                      label="Signed QR code"
                      placeholder="The SignedQRCode string from the reply — this is what the printed QR encodes"
                      value={signedQrCode}
                      onChange={(event) => setSignedQrCode(event.target.value)}
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    label="Or record why the portal refused it"
                    value={refusal}
                    onChange={(event) => setRefusal(event.target.value)}
                  />
                  <Button
                    color="error"
                    disabled={!refusal.trim() || busy}
                    onClick={() =>
                      failure.mutate(
                        { id: invoice.id, errorMessage: refusal.trim() },
                        { onSuccess: () => setRefusal('') },
                      )
                    }
                  >
                    Record refusal
                  </Button>
                </Stack>
              </>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {status?.required && !registered && !cancelled ? (
          <Button
            variant="contained"
            disabled={irn.trim().length !== 64 || busy}
            onClick={() =>
              record.mutate({
                id: invoice.id,
                irn: irn.trim(),
                ackNo: ackNo.trim() || undefined,
                ackDate: ackDate ? new Date(ackDate).toISOString() : undefined,
                signedQrCode: signedQrCode.trim() || undefined,
                ewbNo: ewbNo.trim() || undefined,
              })
            }
          >
            Save IRN
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
